import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";

import {
  fetchAdminStudentFilters,
  fetchAdminStudents,
  fetchAdminStudentStats,
} from "../../services/adminApi";
import { useToast } from "../../hook/useToast";
import AdminDashboardLayout from "./AdminDashboardLayout";
import StatsCard from "./StatsCard";

const DEFAULT_PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  city: "",
  board: "",
  school: "",
  student_class: "",
};

const COLUMNS = [
  { id: "sr", label: "Sr No", align: "left", width: 72 },
  { id: "name", label: "Student Name", align: "left", width: 210 },
  { id: "school", label: "School", align: "left", width: 190 },
  { id: "board", label: "Board", align: "left", width: 92 },
  { id: "class", label: "Class", align: "center", width: 76 },
  { id: "mail", label: "Mail", align: "left", width: 260 },
  { id: "address", label: "Address", align: "left", width: 190 },
  { id: "registered_at", label: "Registration Date", align: "left", width: 190 },
  { id: "tests_taken", label: "Test Taken", align: "center", width: 110 },
  { id: "feedback", label: "Feedback", align: "center", width: 116 },
];

const formatNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }

  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString("en-IN");
};

const formatRegistrationDate = (value) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getInitials = (name = "") => {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "S";
};

const selectSx = {
  height: 38,
  borderRadius: "7px",
  fontSize: 13,
  color: "#2D315F",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E4E6EF" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#D7DAE5" },
  "& .MuiSelect-select": { py: 0, display: "flex", alignItems: "center" },
};

const filterSelectSx = {
  ...selectSx,
  height: 32,
  width: "100%",
  borderRadius: "8px",
  fontSize: 12,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    color: "#171725",
  },
};

const filterMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 190,
      borderRadius: "8px",
      border: "1px solid #EFEFF4",
      boxShadow: "0 14px 34px rgba(45, 49, 95, 0.14)",
      "& .MuiMenuItem-root": {
        minHeight: 28,
        fontSize: 12,
        whiteSpace: "normal",
      },
    },
  },
};

const StudentsPage = ({
  fetchStudentStats = fetchAdminStudentStats,
  fetchStudentFilters = fetchAdminStudentFilters,
  fetchStudents = fetchAdminStudents,
  layoutComponent: LayoutComponent = AdminDashboardLayout,
  pageTitle = "Student Record",
  listTitle = "Students List",
  totalLabel = "Total Students",
}) => {
  const { showToast } = useToast();

  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    boards: [],
    schools: [],
    classes: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [statsLoading, setStatsLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);

    try {
      const response = await fetchStudentStats();
      setStats(response?.data?.data || {});
    } catch {
      showToast("Failed to load student stats", "error");
    } finally {
      setStatsLoading(false);
    }
  }, [fetchStudentStats, showToast]);

  const loadFilterOptions = useCallback(async () => {
    setFiltersLoading(true);

    try {
      const response = await fetchStudentFilters();
      const data = response?.data?.data || {};

      setFilterOptions({
        cities: Array.isArray(data.cities) ? data.cities : [],
        boards: Array.isArray(data.boards) ? data.boards : [],
        schools: Array.isArray(data.schools) ? data.schools : [],
        classes: Array.isArray(data.classes) ? data.classes : [],
      });
    } catch {
      showToast("Failed to load student filters", "error");
    } finally {
      setFiltersLoading(false);
    }
  }, [fetchStudentFilters, showToast]);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    setStudentsError("");

    try {
      const response = await fetchStudents({
        page,
        pageSize,
        search: debouncedSearch,
        filters: appliedFilters,
      });
      const data = response?.data?.data || {};

      setStudents(Array.isArray(data.students) ? data.students : []);
      setPagination({
        page: data.pagination?.page || page,
        page_size: data.pagination?.page_size || pageSize,
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 0,
      });
    } catch {
      setStudents([]);
      setStudentsError("Unable to load students right now.");
      showToast("Failed to load students", "error");
    } finally {
      setStudentsLoading(false);
    }
  }, [appliedFilters, debouncedSearch, fetchStudents, page, pageSize, showToast]);

  useEffect(() => {
    loadStats();
    loadFilterOptions();
  }, [loadFilterOptions, loadStats]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const totalRecords = pagination.total_records || 0;
  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
  const hasDraftFilters = Object.values(draftFilters).some(Boolean);
  const isFilterOpen = Boolean(filterAnchor);

  const paginationItems = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    if (start > 2) {
      items.push("dots-start");
    }

    for (let index = start; index <= end; index += 1) {
      items.push(index);
    }

    if (end < totalPages - 1) {
      items.push("dots-end");
    }

    items.push(totalPages);
    return items;
  }, [page, totalPages]);

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleOpenFilter = (event) => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setDraftFilters(appliedFilters);
    setFilterAnchor(null);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setFilterAnchor(null);
  };

  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    setFilterAnchor(null);
  };

  const handleReviewFeedback = useCallback(
    (student) => {
      const studentName = student?.name || "Student";
      showToast(`${studentName} feedback review is not available yet.`, "info");
    },
    [showToast]
  );

  const renderFilterSelect = (key, label, placeholder, options) => {
    const normalizedOptions = options.map((option) => ({
      value: String(option.value),
      label: option.label,
    }));

    return (
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4A4F63", mb: 0.45 }}>
          {label}
        </Typography>
        <Select
          fullWidth
          displayEmpty
          disabled={filtersLoading}
          value={draftFilters[key]}
          IconComponent={KeyboardArrowDownIcon}
          onChange={(event) => handleFilterChange(key, event.target.value)}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography sx={{ fontSize: 12, color: "#92929D" }}>
                  {placeholder}
                </Typography>
              );
            }

            return normalizedOptions.find((option) => option.value === value)?.label || value;
          }}
          MenuProps={filterMenuProps}
          sx={filterSelectSx}
        >
          <MenuItem value="">All</MenuItem>
          {normalizedOptions.map((option) => (
            <MenuItem key={`${key}-${option.value}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  return (
    <LayoutComponent>
      <Box>
        <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725", mb: 1.4 }}>
          {pageTitle}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 2.2,
          }}
        >
          <StatsCard
            value={
              statsLoading
                ? "--"
                : formatNumber(stats?.total_students)
            }
            label={totalLabel}
            icon="total"
            bgColor="#F5F6FA"
          />
          <StatsCard
            value={
              statsLoading
                ? "--"
                : formatNumber(stats?.students_this_month)
            }
            label="Students This Month"
            icon="month"
            bgColor="#FFF9F1"
          />
          <StatsCard
            value={
              statsLoading
                ? "--"
                : formatNumber(stats?.students_this_year)
            }
            label="Students This Year"
            icon="year"
            bgColor="#F2F6FF"
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "8px",
            bgcolor: "#fff",
            border: "1px solid #EFEFF4",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              minHeight: 58,
              px: 1.5,
              py: 1.2,
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              borderBottom: "1px solid #EFEFF4",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#171725" }}>
                {listTitle}
              </Typography>
              <Chip
                label={formatNumber(totalRecords)}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: "#F0F0F7",
                  color: "#6B7280",
                  borderRadius: "5px",
                }}
              />
              {activeFilterCount > 0 ? (
                <Chip
                  label={`${activeFilterCount} filters`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: "#FFF4E5",
                    color: "#B54708",
                    borderRadius: "5px",
                  }}
                />
              ) : null}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: { xs: "wrap", md: "nowrap" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                IconComponent={KeyboardArrowDownIcon}
                sx={{ ...selectSx, width: 110 }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.9,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                }}
              >
                <TextField
                  placeholder="Search name, email, school..."
                  size="small"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{
                    flex: { xs: 1, sm: "0 0 auto" },
                    width: { xs: "auto", sm: 300 },
                    minWidth: { xs: 0, sm: 260 },
                    "& .MuiOutlinedInput-root": {
                      height: 38,
                      borderRadius: "7px",
                      fontSize: 13,
                      bgcolor: "#fff",
                      "& fieldset": { borderColor: "#E4E6EF" },
                      "&:hover fieldset": { borderColor: "#E4E6EF" },
                      "&.Mui-focused fieldset": { borderColor: "#D7DAE5" },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {studentsLoading && search.trim() !== debouncedSearch ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SearchIcon sx={{ fontSize: 19, color: "#2D315F" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenFilter}
                  sx={{
                    height: 38,
                    minWidth: 96,
                    borderRadius: "7px",
                    border: activeFilterCount ? "1px solid #FFA447" : "1px solid #E4E6EF",
                    bgcolor: activeFilterCount ? "#FFF7ED" : "#fff",
                    color: activeFilterCount ? "#B54708" : "#2D315F",
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "none",
                    px: 1.4,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: activeFilterCount ? "#FFF2E0" : "#F5F6FA",
                      borderColor: activeFilterCount ? "#FFA447" : "#D7DAE5",
                    },
                  }}
                >
                  Filter
                  {activeFilterCount ? (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.8,
                        minWidth: 18,
                        height: 18,
                        px: 0.5,
                        borderRadius: "999px",
                        bgcolor: "#FFA447",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 800,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {activeFilterCount}
                    </Box>
                  ) : null}
                </Button>
              </Box>
            </Box>
          </Box>

          <Popover
            open={isFilterOpen}
            anchorEl={filterAnchor}
            onClose={handleCloseFilter}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 278,
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "10px",
                border: "1px solid #EFEFF4",
                boxShadow: "0 18px 48px rgba(45, 49, 95, 0.16)",
                overflow: "hidden",
              },
            }}
          >
            <Box
              sx={{
                px: 1.4,
                py: 1.05,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #EFEFF4",
                bgcolor: "#FAFBFD",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#171725" }}>
                  Filter Students
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#92929D", mt: 0.15 }}>
                  Choose any field and apply
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleCloseFilter}>
                <CloseIcon sx={{ fontSize: 16, color: "#4A4F63" }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 1.4, display: "grid", gap: 0.9 }}>
              {renderFilterSelect("city", "City", "Select city", filterOptions.cities)}
              {renderFilterSelect("board", "Board", "Select board", filterOptions.boards)}
              {renderFilterSelect("school", "School", "Select school", filterOptions.schools)}
              {renderFilterSelect("student_class", "Class", "Select class", filterOptions.classes)}
            </Box>

            <Box
              sx={{
                px: 1.4,
                py: 1.05,
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.8,
                borderTop: "1px solid #EFEFF4",
                bgcolor: "#fff",
              }}
            >
              <Button
                startIcon={<RestartAltIcon sx={{ fontSize: 17 }} />}
                onClick={handleClearFilters}
                disabled={studentsLoading || (!activeFilterCount && !hasDraftFilters)}
                sx={{
                  height: 30,
                  borderRadius: "7px",
                  color: "#2D315F",
                  border: "1px solid #E4E6EF",
                  bgcolor: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  px: 1.2,
                  "&:hover": { bgcolor: "#F5F6FA", borderColor: "#D7DAE5" },
                }}
              >
                Clear
              </Button>

              <Button
                startIcon={<FilterListIcon sx={{ fontSize: 17 }} />}
                onClick={handleApplyFilters}
                disabled={studentsLoading}
                sx={{
                  height: 30,
                  borderRadius: "7px",
                  bgcolor: "#2D315F",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: 12,
                  px: 1.35,
                  "&:hover": { bgcolor: "#252957" },
                  "&.Mui-disabled": {
                    bgcolor: "#D5D7E2",
                    color: "#fff",
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Popover>

          {studentsError ? (
            <Box sx={{ px: 2, pt: 2 }}>
              <Alert severity="error">{studentsError}</Alert>
            </Box>
          ) : null}

          <TableContainer
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-track": { bgcolor: "#F2F3F7" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#C6CAD8",
                borderRadius: 20,
              },
            }}
          >
            <Table sx={{ minWidth: 1506, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#FAFBFD" }}>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        width: column.width,
                        maxWidth: column.width,
                        height: 45,
                        py: 0,
                        pl: column.align === "left" ? 2.5 : 1.5,
                        pr: 1.5,
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#6B7280",
                        borderBottom: "1px solid #EFEFF4",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {studentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : students.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length}
                      align="center"
                      sx={{ py: 8, color: "#92929D", fontSize: 14 }}
                    >
                      {debouncedSearch || activeFilterCount
                        ? "No students match your search or filters"
                        : "No students found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  students.map((student, index) => {
                    const name = student.name || "Unnamed Student";
                    const rowNumber = (page - 1) * pageSize + index + 1;
                    const registrationDate = formatRegistrationDate(student.registered_at);

                    return (
                      <TableRow
                        key={student.student_id}
                        sx={{
                          height: 68,
                          "& td": { borderBottom: "1px solid #EFEFF4" },
                          "&:hover": { bgcolor: "#FCFCFE" },
                        }}
                      >
                        <TableCell sx={{ pl: 3, fontSize: 13, color: "#111827" }}>
                          {rowNumber}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: "#EEF2FF",
                                color: "#2D315F",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(name)}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#171725",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 13,
                            color: "#171725",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {student.school || "--"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: 13,
                            color: "#4A4F63",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {student.board || "--"}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 13, color: "#4A4F63" }}>
                          {student.class || "--"}
                        </TableCell>
                        <TableCell
                          title={student.email || ""}
                          sx={{
                            fontSize: 13,
                            color: "#4A4F63",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            pr: 2.5,
                          }}
                        >
                          {student.email || "--"}
                        </TableCell>
                        <TableCell
                          title={student.address || ""}
                          sx={{
                            fontSize: 13,
                            color: "#4A4F63",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {student.address || "--"}
                        </TableCell>
                        <TableCell
                          title={registrationDate}
                          sx={{
                            fontSize: 13,
                            color: "#4A4F63",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            pl: 2.5,
                          }}
                        >
                          {registrationDate}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 13, color: "#4A4F63" }}>
                          {student.tests_taken ?? 0}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            type="button"
                            onClick={() => handleReviewFeedback(student)}
                            sx={{
                              minWidth: 82,
                              height: 32,
                              borderRadius: "7px",
                              bgcolor: "#292E5C",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 700,
                              textTransform: "none",
                              px: 1.4,
                              boxShadow: "none",
                              "&:hover": {
                                bgcolor: "#22274F",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              minHeight: 54,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 2,
              py: 1,
              bgcolor: "#fff",
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#92929D" }}>
              Showing {totalRecords === 0 ? 0 : (page - 1) * pageSize + 1}-
              {Math.min(page * pageSize, totalRecords)} of {formatNumber(totalRecords)}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                disabled={page === 1 || studentsLoading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <KeyboardArrowLeftIcon
                  sx={{ fontSize: 20, color: page === 1 ? "#B8BBC8" : "#2D315F" }}
                />
              </IconButton>

              {paginationItems.map((item, index) => {
                if (String(item).startsWith("dots")) {
                  return (
                    <Typography
                      key={`${item}-${index}`}
                      sx={{ fontSize: 14, color: "#171725", px: 0.5 }}
                    >
                      ...
                    </Typography>
                  );
                }

                const selected = item === page;

                return (
                  <Box
                    key={item}
                    onClick={() => {
                      if (!studentsLoading) {
                        setPage(item);
                      }
                    }}
                    sx={{
                      width: selected ? 30 : 26,
                      height: selected ? 30 : 26,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: selected ? "#2D315F" : "transparent",
                      color: selected ? "#fff" : "#171725",
                      fontSize: 13,
                      fontWeight: selected ? 600 : 400,
                      cursor: studentsLoading ? "default" : "pointer",
                    }}
                  >
                    {item}
                  </Box>
                );
              })}

              <IconButton
                size="small"
                disabled={page >= totalPages || studentsLoading}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                <KeyboardArrowRightIcon
                  sx={{
                    fontSize: 20,
                    color: page >= totalPages ? "#B8BBC8" : "#2D315F",
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </LayoutComponent>
  );
};

StudentsPage.propTypes = {
  fetchStudentStats: PropTypes.func,
  fetchStudentFilters: PropTypes.func,
  fetchStudents: PropTypes.func,
  layoutComponent: PropTypes.elementType,
  pageTitle: PropTypes.string,
  listTitle: PropTypes.string,
  totalLabel: PropTypes.string,
};

export default StudentsPage;
